import React, { ReactNode } from 'react';
import { Text, TextVariant } from '@naf/text';
import { ArticleBlockContentGridCol } from '../types/components/ArticleBlockContentGridCol/ArticleBlockContentGridCol';
import { BlockContentContext } from '../BlockContent.utils';

type BlockProps = {
  children?: ReactNode;
  node?: {
    _key?: string;
    [key: string]: any;
  };
};

const ArticleWrapper = ({
  children,
  styleName,
  isArticle,
}: {
  children?: ReactNode;
  styleName: string;
  isArticle: boolean;
}) =>
  isArticle ? (
    <ArticleBlockContentGridCol className={`article_${styleName}`}>{children}</ArticleBlockContentGridCol>
  ) : (
    children
  );

export const block = (context: BlockContentContext = BlockContentContext.Stripped) => {
  const isArticle = context === BlockContentContext.Article;
  return {
    ingress: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="ingress">
        <Text variant={TextVariant.Ingress}>{children}</Text>
      </ArticleWrapper>
    ),
    h1: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="h1">
        <Text variant={TextVariant.Header1}>{children}</Text>
      </ArticleWrapper>
    ),
    h2: ({ children, node }: BlockProps) => {
      const key = node?._key;
      const headingText = Array.isArray(node?.children)
        ? node.children.map((child: { text?: string }) => child?.text ?? '').join('')
        : '';
      const headingId = `${headingText.replace(/ /g, '_').trim().toLowerCase()}_${key}`;

      return (
        <ArticleWrapper key={key} isArticle={isArticle} styleName="h2">
          <Text tag="h2" id={headingId} variant={TextVariant.Header2}>
            {children}
          </Text>
        </ArticleWrapper>
      );
    },
    h3: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="h3">
        <Text variant={TextVariant.Header3}>{children}</Text>
      </ArticleWrapper>
    ),
    normal: ({
      children,
      value,
      node,
    }: {
      children?: ReactNode;
      value?: { children?: { _type?: string }[] };
      node?: { children?: { _type?: string }[] };
    }) => {
      // A "normal" paragraph that embeds a block-level object (image, YouTube, …) must not render as <p>:
      // a <div>/<figure> can't be a descendant of <p>, so the browser auto-closes the <p> before the embed
      // — desyncing the SSR DOM from React's tree → hydration mismatch (React #418, "In HTML, <div> cannot
      // be a descendant of <p>"). Render such paragraphs as <div>; keep <p> for plain text (NOA-3224).
      const blockChildren = (value ?? node)?.children;
      const hasEmbeddedObject = Array.isArray(blockChildren) && blockChildren.some((child) => child?._type !== 'span');
      return (
        <ArticleWrapper isArticle={isArticle} styleName="normal">
          {hasEmbeddedObject ? <div>{children}</div> : <p>{children}</p>}
        </ArticleWrapper>
      );
    },
    blockquote: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="blockquote">
        <blockquote>{children}</blockquote>
      </ArticleWrapper>
    ),
    h4: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="h4">
        <h4>{children}</h4>
      </ArticleWrapper>
    ),
    h5: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="h5">
        <h5>{children}</h5>
      </ArticleWrapper>
    ),
    h6: ({ children }: { children?: ReactNode; _key?: string }) => (
      <ArticleWrapper isArticle={isArticle} styleName="h6">
        <h6>{children}</h6>
      </ArticleWrapper>
    ),
  };
};
